import { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react'

import * as Styled from './styled'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'

import TtsListItem from './Item'
import { ITtsItem } from 'types/text-to-speech'
import TtsAddButton from './AddButton'
import { useSelector } from 'react-redux'
import { ttsThunks } from 'store/features/tts/thunks'
import { ttsSelectors } from 'store/features/tts/selectors'
import {
  useGetActiveTimelineTtsAssetId,
  useGetActiveTtsItemTimelineId
} from './useGetActiveTimelineTts'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { dispatch } from 'store'
import { LoadingSkeleton } from 'common-components'
import { createArrayWithKeys } from 'utils'
import { editorThunks } from 'store/features/editor/thunks'
import { PROJECT_AUDIO_TYPES } from 'consts/audio'

const TtsList: React.FC = () => {
  const activeTimelineTtsId = useGetActiveTimelineTtsAssetId()
  const ttsItems: ITtsItem[] = useSelector(ttsSelectors.ttsItems)
  const ttsAudios = useSelector(projectDataSelectors.ttsAudios)
  const isSingleDraftTtsItemOnly = useSelector(
    ttsSelectors.isSingleDraftTtsItemOnly
  )
  const isGenerationInProcess = useSelector(ttsSelectors.isGenerationInProcess)

  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const [activeTtsId, setActiveTtsId] = useState<number | null>(
    activeTimelineTtsId
  )
  const getActiveTtsItemTimelineId = useGetActiveTtsItemTimelineId()

  const skeletonItems = useMemo(() => {
    return createArrayWithKeys(5)
  }, [])

  const getIsActiveTtsItem = useCallback(
    (id: number) =>
      (activeTtsId === id || isSingleDraftTtsItemOnly) &&
      !isGenerationInProcess,
    [activeTtsId, isSingleDraftTtsItemOnly, isGenerationInProcess]
  )

  const selectTtsItem = useCallback(
    (id: number) => {
      const activeTtsItemTimelineId = getActiveTtsItemTimelineId(id)
      if (activeTtsId !== id) {
        setActiveTtsId(id)
        dispatch(
          editorThunks.setActiveTimelineItems([
            {
              id: activeTtsItemTimelineId,
              type: PROJECT_AUDIO_TYPES.TTS
            }
          ])
        )
      }
    },
    [activeTtsId, getActiveTtsItemTimelineId]
  )
  // to reset the active audio id to active tts id, do not add activeTtsId to deps//
  useEffect(() => {
    if (activeTtsId !== activeTimelineTtsId && !!activeTimelineTtsId) {
      setActiveTtsId(activeTimelineTtsId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimelineTtsId])

  useEffect(() => {
    if (!ttsAudios.length && !ttsItems.length) {
      dispatch(ttsThunks.setTtsInitialDraftItemData())
    }
  }, [ttsAudios.length, ttsItems.length])
  return (
    <>
      <Styled.CustomTextWithIconTooltip
        title={translate(T.TEXTS)}
        tooltipText={translate(T.TTS_TEXTS_TOOLTIP)}
        gap={4}
        textProps={{
          type: 'subtitle',
          color: 'secondarySemiDark',
          fontWeight: '700'
        }}
        iconProps={{
          color: 'secondarySemiLight',
          customSize: 20
        }}
        tooltipPosition="right"
        showMobileTooltip
      />
      <Styled.TtsListWrapper ref={scrollableContainerRef}>
        {!ttsItems.length
          ? skeletonItems.map(item => (
              <Styled.SkeletonWrapper key={item.key}>
                <LoadingSkeleton />
              </Styled.SkeletonWrapper>
            ))
          : ttsItems.map(item => (
              <TtsListItem
                key={item.data.id}
                ttsData={item.data}
                onClick={selectTtsItem}
                isActive={getIsActiveTtsItem(item.data.id)}
                scrollableParentRef={scrollableContainerRef}
              />
            ))}
        <TtsAddButton setActiveTtsId={setActiveTtsId} />
      </Styled.TtsListWrapper>
    </>
  )
}

export default memo(TtsList)
